import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Divider, Grid, Heading, Stack, Text } from '@eneco/sparky';
import { ResponseModels_Products_Interruption_InterruptionModel } from '@monorepo-types/dc';
import { wrap } from '@sitecore/common';
import { WarmthInterruptionsRendering } from '@sitecore/types/manual/WarmthInterruptions';

type Props = {
  fields: WarmthInterruptionsRendering['fields'];
  interruptions: ResponseModels_Products_Interruption_InterruptionModel['data'];
};

export const WarmthInterruptionsDialog: FC<Props> = ({ fields, interruptions }) => {
  const {
    areaLabel,
    startDateLabel,
    endDateLabel,
    effectLabel,
    typeLabel,
    unplannedText,
    plannedText,
    noInterruptionsContent,
    additionalInformationContent,
    disclaimerContent,
  } = fields ?? {};
  const { date } = useFormatter();

  if (!interruptions?.length) {
    return wrap(noInterruptionsContent, <RichText html={noInterruptionsContent?.value} />);
  }

  const formatDate = (datestring?: string | null) => {
    if (datestring) {
      const time = datestring.includes('T') ? ` / ${date.hourLong(new Date(datestring))}` : '';
      return `${date.medium(new Date(datestring))}${time}`;
    }
  };

  return (
    <Stack gap="6">
      {interruptions?.map((item, index) => (
        <Stack gap="6" key={index}>
          <Stack gap="2">
            <Heading size="XS" as="h3">
              {item.address?.city}
            </Heading>
            <Heading size="3XS" as="h4" color="textLowEmphasis">
              {`${wrap(areaLabel)} ${item.address?.postalCode} ${item.address?.street}`}
            </Heading>
          </Stack>
          <Grid gridTemplateColumns="1fr 1fr" gap="2">
            <Grid.Item>
              <Text weight="bold">{wrap(startDateLabel)}</Text>
            </Grid.Item>
            <Grid.Item>{formatDate(item.startDate)}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{wrap(endDateLabel)}</Text>
            </Grid.Item>
            <Grid.Item>{formatDate(item.expectedEndDate)}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{wrap(effectLabel)}</Text>
            </Grid.Item>
            <Grid.Item>{item.effect}</Grid.Item>
            <Grid.Item>
              <Text weight="bold">{wrap(typeLabel)}</Text>
            </Grid.Item>
            <Grid.Item>{item.isPlanned ? wrap(plannedText) : wrap(unplannedText)}</Grid.Item>
          </Grid>
          <Divider />
        </Stack>
      ))}
      {wrap(additionalInformationContent, <RichText html={additionalInformationContent?.value} />)}
      <Divider />
      <Text size="BodyS" color="textLowEmphasis">
        {wrap(disclaimerContent, <RichText html={disclaimerContent?.value} />)}
      </Text>
    </Stack>
  );
};
